// Dev-only React-fiber introspection (React 19). Reads the component tree under
// a DOM node to identify MUI components and what they're composed of — like a
// tiny React DevTools. Names survive because Vite dev keeps function names and
// MUI components are named forwardRefs.
import { INSPECTABLE, LAYOUTS } from '@/designs-dev/inspect/ds-registry';
import { isLocalComponent, localFileFor } from '@/designs-dev/inspect/local';
import WrappedCard from '@/components/wrapped/Card';
import WrappedAppBar from '@/karehero-app/shell/AppBar';

type Fiber = any;

/**
 * Worth drawing as a layer: a registry component, or any component the
 * prototypes own. Naming a component is a filesystem fact; the registry only
 * says what it means (see local.ts).
 */
const isComponent = (name: string | null): name is string =>
  !!name && (INSPECTABLE.has(name) || isLocalComponent(name));

// Names that a prototype-owned component shares with a raw MUI component
// (e.g. the wrapped Card forks MUI's Card). The registry grades the wrapped
// one; a raw MUI instance of the same name is pipe-through and must NOT borrow
// that grade. The name alone can't tell them apart — only the fiber's type
// identity can. Add to this map if another wrapped component ever collides.
const NAME_COLLISIONS: Record<string, unknown> = {
  Card: WrappedCard,
  AppBar: WrappedAppBar,
};

export type InspectionProp = {
  name: string;
  value: string; // stringified for display ('h3', 'true', 'ƒ', '<Button>')
  kind: 'primitive' | 'element' | 'function';
};

export type Inspection = {
  name: string;
  props: InspectionProp[]; // every prop passed at the call site (minus sx)
  madeOf: string[]; // e.g. ["Checkbox", "Typography(h5)", "Typography(body1) ×2", "Avatar"]
  hasSx: boolean; // true = this instance carries a per-instance sx prop
  // true = that sx carries visual keys (colour, border, radius, type). Layout sx
  // is the only mechanism since MUI 9 removed system props, so it isn't flagged.
  hasVisualSx: boolean;
  // The visual part of that sx as a comparable string, so the tree can tell
  // when the same look is applied in more than one place. Absent when there's
  // no visual sx, or when it's a function and can't be read.
  visualSxKey?: string;
  sxText: string; // human-readable summary of the sx
  // Raw inline `style` overrides (on the root or on a slot like InputProps),
  // which bypass the theme entirely — flagged like sx.
  hasStyle: boolean;
  styleText: string;
  domNode: HTMLElement | null;
  // How this instance reached its parent component: through which prop
  // ('children', 'footer', …). Absent = the parent's own composition (or a
  // canvas-hover selection, where provenance isn't computed).
  origin?: { prop: string };
  // A layout primitive (Stack/Box/Grid) that's carrying layout intent, shown in
  // the tree to reveal what controls a screen's structure. Not a violation —
  // system-prop layout is the sanctioned path. layoutProps is the curated set
  // (spacing/direction/p/m/gap/flex…) surfaced inline; absent on real DS nodes.
  isLayout?: boolean;
  layoutProps?: InspectionProp[];
  // True when this fiber shares a name with a wrapped registry component but is
  // actually the raw MUI component (matched by type identity). It's a clean
  // pipe-through — the panel must not apply the wrapped component's grade/drift.
  muiBase?: boolean;
  // The file this component lives in, for a prototype-owned one. Read from the
  // local scan rather than the registry, so a component with no card still says
  // where it is.
  file?: string;
};

// Summarise an sx prop so the inspector can say exactly what's violating the rule.
// Lists each style key, with its value when primitive (e.g. "p: 2, bgcolor: custom.coral.600").
// Visual sx keys, the same set as scripts/ds-sx-audit.mjs. Anything else
// (padding, gap, flex, width, position) is layout and belongs in sx.
const VISUAL_SX = new Set([
  'color',
  'bgcolor',
  'backgroundColor',
  'background',
  'border',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'borderColor',
  'borderRadius',
  'borderWidth',
  'borderStyle',
  'boxShadow',
  'fontWeight',
  'fontSize',
  'fontFamily',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textAlign',
  'textTransform',
  'textDecoration',
  'opacity',
  'filter',
  'outline',
  'cursor',
  'transition',
  'whiteSpace',
  'textOverflow',
  'visibility',
]);

// Recurses into selector blocks ('&:hover', '& .x'), since hover colours are
// theme work too. A function sx can't be read, so it counts as visual.
const hasVisualKeys = (sx: any): boolean => {
  if (!sx) return false;
  if (typeof sx === 'function') return true;
  const objs = Array.isArray(sx) ? sx : [sx];
  return objs.some(
    (o) =>
      typeof o === 'function' ||
      (o &&
        typeof o === 'object' &&
        Object.entries(o).some(([k, v]) =>
          k.startsWith('&') ? hasVisualKeys(v) : VISUAL_SX.has(k),
        )),
  );
};

// The visual keys and their values, sorted, selector blocks included. Two
// elements with the same key carry the same look.
const visualSxKey = (sx: any): string | undefined => {
  if (!sx || typeof sx === 'function') return undefined;
  const out: string[] = [];
  const collect = (o: any, prefix: string): boolean => {
    if (typeof o === 'function') return false;
    if (!o || typeof o !== 'object') return true;
    for (const [k, v] of Object.entries(o)) {
      if (k.startsWith('&')) {
        if (!collect(v, `${prefix}${k} `)) return false;
      } else if (VISUAL_SX.has(k)) {
        if (typeof v === 'function') return false;
        out.push(`${prefix}${k}:${JSON.stringify(v)}`);
      }
    }
    return true;
  };
  const objs = Array.isArray(sx) ? sx : [sx];
  if (!objs.every((o) => collect(o, ''))) return undefined;
  return out.length > 0 ? out.sort().join(';') : undefined;
};

const summarizeSx = (sx: any): string => {
  if (!sx) return '';
  if (typeof sx === 'function') return '(theme) => …';
  const objs = Array.isArray(sx) ? sx : [sx];
  const parts: string[] = [];
  for (const o of objs) {
    if (o && typeof o === 'object') {
      for (const [k, v] of Object.entries(o)) {
        parts.push(
          v !== null && (typeof v === 'object' || typeof v === 'function')
            ? k
            : `${k}: ${v}`,
        );
      }
    } else if (typeof o === 'function') {
      parts.push('(theme) => …');
    }
  }
  return parts.join(', ');
};

// Collect raw inline `style` overrides — on the root and on common slots
// (InputProps/inputProps/slotProps.*) — so they're surfaced like sx. Inline
// style bypasses the theme entirely, so it's a violation too.
const summarizeStyle = (props: any): string => {
  if (!props) return '';
  const found: string[] = [];
  const add = (label: string, styleObj: any) => {
    if (styleObj && typeof styleObj === 'object') {
      const s = summarizeSx(styleObj);
      if (s) found.push(label ? `${label}: { ${s} }` : s);
    }
  };
  add('', props.style);
  add('InputProps', props.InputProps?.style);
  add('inputProps', props.inputProps?.style);
  if (props.slotProps && typeof props.slotProps === 'object') {
    for (const [slot, val] of Object.entries(props.slotProps)) {
      add(`slotProps.${slot}`, (val as any)?.style);
    }
  }
  return found.join(' · ');
};

const getFiber = (node: any): Fiber | null => {
  const key = Object.keys(node).find((k) => k.startsWith('__reactFiber$'));
  return key ? node[key] : null;
};

// Vite's dep pre-bundle (esbuild) dedupes top-level names with a numeric
// suffix — MUI's Typography arrives as 'Typography2'. Strip it so registry
// lookups match. Local source components keep their names and never carry
// the suffix.
const deSuffix = (n: string | null): string | null =>
  n ? n.replace(/\d+$/, '') : n;

// The class MUI puts on its own root node, which is the one place a component
// reliably says what it is.
const MUI_ROOT_CLASS = /\bMui([A-Za-z0-9]+)-root\b/;

// MUI components are forwardRefs with a named inner render fn.
//
// Except Stack, which MUI builds from the Grid2 factory: its render function is
// literally named `Grid2`, and deSuffix then turned it into `Grid`. Every Stack in
// the app was labelled Grid in the layers tree — which is not a cosmetic problem,
// because a person looking for the Stack they wrote never finds it and concludes
// the tree is missing layers.
//
// So when the render name disagrees with the class MUI emitted, the class wins. It
// is checked only for the Grid/Stack pair rather than universally: most components
// emit several Mui*-root classes (a Card is also a Paper) and picking off the
// regex would rename half the tree by luck of ordering.
const CLASS_OVER_RENDER = new Set(['Grid', 'Grid2']);

const nameOf = (fiber: Fiber): string | null => {
  const t = fiber?.type;
  if (!t || typeof t === 'string') return null; // host element (div, span…)
  const raw =
    t.render?.displayName ||
    t.render?.name ||
    t.displayName ||
    t.muiName ||
    t.name ||
    null;
  if (!raw) return null;
  if (CLASS_OVER_RENDER.has(raw)) {
    const cls = hostNodeOf(fiber)?.className?.toString?.() ?? '';
    const m = MUI_ROOT_CLASS.exec(cls);
    if (m) return m[1];
  }
  return deSuffix(raw);
};

// Loose React element check (works across React 18/19 $$typeof symbols).
const isElement = (v: any): boolean =>
  !!v &&
  typeof v === 'object' &&
  !!v.$$typeof &&
  v.type !== undefined &&
  'props' in v;

const elementName = (el: any): string => {
  const t = el?.type;
  if (typeof t === 'string') return t;
  return (
    deSuffix(
      t?.render?.displayName ||
        t?.render?.name ||
        t?.displayName ||
        t?.muiName ||
        t?.name,
    ) || '…'
  );
};

// The first props a dev reaches for, in the order they matter for handover.
const PROP_PRIORITY = ['variant', 'color', 'size'];

// Every prop passed at the call site — primitives with their values, slots as
// <Element>, callbacks as ƒ. sx is excluded (it gets the violation treatment)
// and children's contents are described by origin badges in the layers tree.
const structuredProps = (fiber: Fiber): InspectionProp[] => {
  const p = fiber?.memoizedProps ?? {};
  const out: InspectionProp[] = [];
  for (const [name, v] of Object.entries(p)) {
    if (name === 'sx' || name === 'children' || v === undefined) continue;
    if (typeof v === 'function') {
      out.push({ name, value: 'ƒ', kind: 'function' });
    } else if (isElement(v)) {
      out.push({ name, value: `<${elementName(v)}>`, kind: 'element' });
    } else if (Array.isArray(v) && v.some(isElement)) {
      out.push({ name, value: `${v.length} elements`, kind: 'element' });
    } else if (v === null || typeof v !== 'object') {
      out.push({ name, value: String(v), kind: 'primitive' });
    } else {
      out.push({ name, value: '{…}', kind: 'primitive' });
    }
  }
  return out.sort((a, b) => {
    const pa = PROP_PRIORITY.indexOf(a.name);
    const pb = PROP_PRIORITY.indexOf(b.name);
    if (pa !== -1 || pb !== -1)
      return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
    return a.name.localeCompare(b.name);
  });
};

const variantSuffix = (fiber: Fiber): string => {
  const v = fiber?.memoizedProps?.variant;
  return v ? `(${v})` : '';
};

// Nearest host DOM node at or below a (component) fiber, for the highlight
// rect. Full DFS over the subtree (not just the first-child chain — a
// component's first branch can dead-end without a host, e.g. a null-rendering
// child), with a fallback to the alternate fiber: tree walks can land on the
// stale copy of React's double-buffered tree, whose child pointers may be gone.
const hostNodeOf = (fiber: Fiber): HTMLElement | null => {
  const dfs = (f: Fiber): HTMLElement | null => {
    if (!f) return null;
    if (typeof f.type === 'string' && f.stateNode)
      return f.stateNode as HTMLElement;
    let child = f.child;
    while (child) {
      const found = dfs(child);
      if (found) return found;
      child = child.sibling;
    }
    return null;
  };
  return dfs(fiber) ?? (fiber?.alternate ? dfs(fiber.alternate) : null);
};

// Walk descendants collecting distinct registry component names (excluding the
// root), with Typography variant hints and ×N counts.
const composedOf = (root: Fiber): string[] => {
  const counts = new Map<string, number>();
  const visit = (f: Fiber, isRoot: boolean) => {
    if (!f) return;
    const n = nameOf(f);
    if (!isRoot && isComponent(n)) {
      const label = n === 'Typography' ? `Typography${variantSuffix(f)}` : n;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    visit(f.child, false);
    if (!isRoot) visit(f.sibling, false);
  };
  visit(root.child, false);
  return [...counts.entries()].map(([label, n]) =>
    n > 1 ? `${label} ×${n}` : label,
  );
};

const describe = (fiber: Fiber): Inspection => {
  const name = nameOf(fiber) as string;
  const label =
    name === 'Typography' ? `Typography${variantSuffix(fiber)}` : name;
  const sx = fiber?.memoizedProps?.sx;
  const styleText = summarizeStyle(fiber?.memoizedProps);
  // A colliding name (e.g. Card) is the prototype wrapped component only when
  // the fiber's type matches it; otherwise it's the raw MUI component (pipe).
  const muiBase =
    name in NAME_COLLISIONS && fiber?.type !== NAME_COLLISIONS[name];
  // Curated layout/spacing props from the call site, so the design panel can
  // prepopulate its Layout & spacing selects for any selected component — not
  // just layout containers (which get these set again in the tree builder).
  const layoutProps = layoutPropsOf(fiber);
  // Where the prototypes keep it, for a component the registry has no card for:
  // the panel can point at the file even when nobody has written the notes.
  const localFile = muiBase ? undefined : localFileFor(name);
  return {
    name: label,
    props: structuredProps(fiber),
    madeOf: composedOf(fiber),
    hasSx: !!sx,
    hasVisualSx: hasVisualKeys(sx),
    ...(visualSxKey(sx) ? { visualSxKey: visualSxKey(sx) } : {}),
    sxText: summarizeSx(sx),
    hasStyle: !!styleText,
    styleText,
    domNode: hostNodeOf(fiber),
    ...(layoutProps.length > 0 ? { layoutProps } : {}),
    ...(muiBase ? { muiBase: true } : {}),
    ...(localFile ? { file: localFile } : {}),
  };
};

// From a DOM node, pick the component to inspect. Climbing up, we keep promoting to
// the OUTERMOST registry component — so a wrapper (or pipe component) is selected as
// a whole, not drilled into. LAYOUTS are boundaries: we stop at the first one, so
// inside a layout (a bottom sheet, the phone frame…) you still select the inner
// component. Hovering a layout's own chrome (with no inner component) selects the layout.
export const inspectNode = (node: HTMLElement | null): Inspection | null => {
  if (!node) return null;
  let fiber = getFiber(node);
  let selected: Fiber | null = null;
  while (fiber) {
    const name = nameOf(fiber);
    if (isComponent(name)) {
      if (LAYOUTS.has(name)) {
        // Hovering the layout's own chrome selects the layout. A selection
        // that renders the layout's own DOM node is its styled alias (Card
        // is styled Paper) — that's chrome too, not a piece inside it.
        if (!selected || hostNodeOf(selected) === hostNodeOf(fiber))
          selected = fiber;
        break; // layout boundary: don't climb past it
      }
      selected = fiber; // promote to this outer (non-layout) registry component
    }
    fiber = fiber.return;
  }
  return selected ? describe(selected) : null;
};

// Strip a Typography(variant) label back to the bare registry key.
export const baseName = (label: string): string =>
  label.replace(/\s*\(.*\)$/, '').replace(/\s*×\d+$/, '');

// A node in the layers tree: a registry component instance with its nested
// registry components as children. Non-registry fibers are transparent — their
// children are lifted up to the nearest registry ancestor.
export type LayerNode = {
  id: string; // structural path ("0.2.1"), stable while the tree shape is
  inspection: Inspection;
  // Which of the parent component's props this instance arrived through
  // ('children', 'footer', …). Absent = the parent's own composition.
  origin?: { prop: string };
  children: LayerNode[];
};

// Elements found in a component's props, mapped to the prop they came in
// through — the basis for slot provenance in the layers tree.
type SlotEntry = { el: any; prop: string };

const slotEntriesOf = (fiber: Fiber): SlotEntry[] => {
  const props = fiber?.memoizedProps;
  if (!props || typeof props !== 'object') return [];
  const entries: SlotEntry[] = [];
  for (const [prop, v] of Object.entries(props)) {
    if (isElement(v)) {
      entries.push({ el: v, prop });
    } else if (Array.isArray(v)) {
      for (const item of v)
        if (isElement(item)) entries.push({ el: item, prop });
    }
  }
  return entries;
};

// Does fiber F render one of the parent's slot elements? Matched by the
// element's props object identity (current or alternate fiber).
const slotOf = (f: Fiber, slots: SlotEntry[]): string | null => {
  if (slots.length === 0) return null;
  for (const { el, prop } of slots) {
    if (
      el.props === f.memoizedProps ||
      (f.alternate && el.props === f.alternate.memoizedProps)
    )
      return prop;
  }
  return null;
};

// Phone chrome that never shows as its own node in the layers tree, in two
// flavours:
// - TRANSPARENT: skip the node but keep its subtree — the frame wraps the real
//   screen, so its children are exactly what we want to see lifted up.
// - PRUNED: drop the node AND its whole subtree — the status bar, home
//   indicator and on-screen keyboard are pure system chrome whose internals
//   (the clock, signal/wifi/battery, the home pill) are never design-system
//   surface. Left transparent they'd leak orphan nodes — e.g. the status bar's
//   '9:41' Typography — so we never descend into them.
const LAYER_TRANSPARENT = new Set(['PhoneFrame']);
// Pure system chrome, and Loft's own overlay — which renders inside the screen
// root, so naming every prototype component surfaced it as a layer of the
// design. It is the thing drawing the drill highlight, not part of what is
// being drilled.
const LAYER_PRUNED = new Set([
  'StatusBar',
  'HomeIndicator',
  'MobileKeyboard',
  'InspectOverlay',
]);

// MUI layout primitives. They aren't registry components (they carry no
// pipe/wrapped/theme grade), but they ARE what controls a screen's structure —
// so the layers tree surfaces them when they're doing layout work.
const LAYOUT_PRIMITIVES = new Set([
  'Stack',
  'Box',
  'Grid',
  'Grid2',
  'Container',
]);

// System props that mean "this container is doing layout" (ordered for a stable
// inline read: structure first, then spacing). A primitive carrying none of
// these is a bare wrapper — kept transparent so the tree isn't flooded with
// styling-only or grouping-only Boxes.
const LAYOUT_PROPS = [
  'direction',
  'spacing',
  'gap',
  'rowGap',
  'columnGap',
  'display',
  'flex',
  'flexDirection',
  'flexWrap',
  'flexGrow',
  'flexShrink',
  'alignItems',
  'alignContent',
  'alignSelf',
  'justifyContent',
  'justifyItems',
  'justifySelf',
  'columns',
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridColumn',
  'gridRow',
  'p',
  'px',
  'py',
  'pt',
  'pb',
  'pl',
  'pr',
  'padding',
  'paddingX',
  'paddingY',
  'm',
  'mx',
  'my',
  'mt',
  'mb',
  'ml',
  'mr',
  'margin',
  'marginX',
  'marginY',
];

// The layout-relevant props actually present on a fiber, in LAYOUT_PROPS order.
const layoutPropsOf = (fiber: Fiber): InspectionProp[] => {
  const p = fiber?.memoizedProps ?? {};
  const out: InspectionProp[] = [];
  for (const name of LAYOUT_PROPS) {
    const v = p[name];
    if (v !== undefined && v !== null && typeof v !== 'object')
      out.push({ name, value: String(v), kind: 'primitive' });
  }
  return out;
};

// Build the registry-level component tree under a DOM node (the phone screen)
// for the layers panel. Unlike inspectNode (which promotes to the OUTERMOST
// component), the tree shows what's inside wrappers and layouts.
export const buildLayerTree = (rootEl: HTMLElement | null): LayerNode[] => {
  if (!rootEl) return [];
  const rootFiber = getFiber(rootEl);
  if (!rootFiber) return [];

  // Stale double-buffer fibers can have a null child pointer while their
  // alternate still holds the live subtree — prefer whichever has children.
  const childOf = (f: Fiber): Fiber | null =>
    f?.child ?? f?.alternate?.child ?? null;

  const build = (
    fiber: Fiber,
    id: string,
    origin: string | null,
  ): LayerNode => ({
    id,
    inspection: describe(fiber),
    ...(origin ? { origin: { prop: origin } } : {}),
    children: collect(childOf(fiber), fiber, `${id}.`),
  });

  // Gather the nearest registry descendants of `start` (and its siblings).
  // styled()/memo chains render the same component name twice for one DOM
  // node — those duplicates are treated as transparent rather than nested.
  // Slot provenance: a fiber that renders one of the parent's prop elements
  // sets the origin context, which flows down through transparent fibers
  // until the first registry node consumes it.
  const collect = (
    start: Fiber,
    parent: Fiber,
    prefix: string,
  ): LayerNode[] => {
    const out: LayerNode[] = [];
    const parentName = nameOf(parent);
    const parentHost = hostNodeOf(parent);
    const slots = slotEntriesOf(parent);
    const walk = (f: Fiber, inherited: string | null) => {
      if (!f) return;
      const n = nameOf(f);
      // Pure system chrome: prune the whole subtree so its internals never
      // surface as orphan nodes. Still visit siblings.
      if (n && LAYER_PRUNED.has(n)) {
        walk(f.sibling, inherited);
        return;
      }
      const origin = slotOf(f, slots) ?? inherited;
      // A component rendering the parent's own DOM node is an alias, not
      // composition: styled()/memo chains repeat the parent's name, and MUI
      // bases components on others (Card is styled Paper). Either way it's
      // implementation detail — transparent in the tree.
      const isAlias = !!n && !!parentName && hostNodeOf(f) === parentHost;
      const layout = n && LAYOUT_PRIMITIVES.has(n) ? layoutPropsOf(f) : [];
      if (isComponent(n) && !LAYER_TRANSPARENT.has(n) && !isAlias) {
        out.push(build(f, `${prefix}${out.length}`, origin));
      } else if (layout.length > 0 && !isAlias) {
        // A layout primitive doing real layout work: show it (lighter, in the
        // tree), with its layout props inline, and keep building its subtree.
        const node = build(f, `${prefix}${out.length}`, origin);
        node.inspection.isLayout = true;
        node.inspection.layoutProps = layout;
        out.push(node);
      } else {
        walk(childOf(f), origin);
      }
      walk(f.sibling, inherited);
    };
    walk(start, null);
    return out;
  };

  return collect(childOf(rootFiber), rootFiber, '');
};

// --- Direct editing ----------------------------------------------------------
//
// What the on-canvas handles need to word an instruction: which component a host
// element belongs to, which file wrote it, and whether its siblings came from a
// list. All read off the fiber, so nothing has to be annotated by hand.

const isNamed = (n: string | null): n is string =>
  isComponent(n) || (!!n && LAYOUT_PRIMITIVES.has(n));

/**
 * The component a host element renders, and the prototype file whose JSX put it
 * there: `Stack in OnboardingStep`, with OnboardingStep's file. A reorder is made
 * in that file, so the instruction has to name it rather than the Stack's.
 */
export const ownerOf = (el: HTMLElement): { name: string; file?: string } => {
  let f = getFiber(el);
  let first: string | null = null;
  while (f) {
    const n = nameOf(f);
    if (isNamed(n)) {
      first ??= n;
      const file = localFileFor(n);
      if (file) return { name: first === n ? n : `${first} in ${n}`, file };
    }
    f = f.return;
  }
  return { name: first ?? el.tagName.toLowerCase() };
};

/**
 * A child as a person would point at it: its outermost component and the start
 * of its text. Two Cards share a name, so the name alone cannot say which moved.
 */
export const layerLabel = (el: HTMLElement): string => {
  let f = getFiber(el);
  let name: string | null = null;
  while (f && hostNodeOf(f) === el) {
    const n = nameOf(f);
    if (isNamed(n)) name = n;
    f = f.return;
  }
  const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ');
  const snip = text.length > 24 ? `${text.slice(0, 24)}…` : text;
  const base = name ?? el.tagName.toLowerCase();
  return snip ? `${base} "${snip}"` : base;
};

/**
 * Whether a child arrived from a `.map`. A keyed fiber between the child's host
 * node and its parent's means the order lives in an array (content.ts or
 * fixtures), not in the JSX, and the instruction says so.
 */
export const isListItem = (
  child: HTMLElement,
  parent: HTMLElement,
): boolean => {
  let f = getFiber(child);
  while (f && f.stateNode !== parent) {
    if (f.key != null) return true;
    f = f.return;
  }
  return false;
};
